import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { BiSave } from 'react-icons/bi';

import { API_BASE_URL } from '../utils/urls.js';

const formatRoleName = (roleName = "") => {
    return roleName.replace(/_/g, " ").split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
};

const TaskForm = () => {
    const { taskId } = useParams();
    const isEditing = !!taskId;
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        task_title: '',
        task_description: '',
        start_time: '',
        end_time: '',
        case_id: '',
        user_id: '',
        status: 'pending'
    });
    
    // --- NEW STATE & VARIABLES ---
    const currentUser = JSON.parse(localStorage.getItem('user'));
    const currentUserId = currentUser?.id;
    const isFieldAgent = currentUser?.roleName === 'field_agent';
    const isAssignedAgent = isEditing && isFieldAgent && (parseInt(formData.user_id) === parseInt(currentUserId));
    const canManageFullForm = !isFieldAgent; // Only Admin/Manager/SuperAdmin can edit full form
    
    const [cases, setCases] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitStatus, setSubmitStatus] = useState('');
    // ----------------------------

    const toLocalDatetime = (isoDate) => {
        if (!isoDate) return '';
        const date = new Date(isoDate);
        date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
        return date.toISOString().slice(0, 16);
    };

    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Authentication token not found.');
                setLoading(false);
                return;
            }
            try {
                // Fetch cases and users only if needed for form dropdowns (i.e., not a pure Field Agent update)
                if (canManageFullForm || !isEditing) {
                     const [casesRes, usersRes] = await Promise.all([
                        axios.get(`${API_BASE_URL}/cases`, { headers: { Authorization: `Bearer ${token}` } }),
                        axios.get(`${API_BASE_URL}/users/assignable`, { headers: { Authorization: `Bearer ${token}` } }),
                    ]);
                    setCases(casesRes.data.data);
                    setUsers(usersRes.data);
                }

                if (isEditing) {
                    const taskRes = await axios.get(`${API_BASE_URL}/tasks/${taskId}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    const taskData = taskRes.data;

                    // Redirection check: Prevent Field Agent from opening tasks not assigned to them
                    if (isFieldAgent && parseInt(taskData.user_id) !== parseInt(currentUserId)) {
                         throw new Error("Access denied. You can only edit your own assigned tasks.");
                    }

                    setFormData({
                        task_title: taskData.task_title,
                        task_description: taskData.task_description,
                        start_time: toLocalDatetime(taskData.start_time),
                        end_time: toLocalDatetime(taskData.end_time),
                        case_id: taskData.case_id,
                        user_id: taskData.user_id,
                        status: taskData.status || 'pending',
                    });
                }
            } catch (err) {
                console.error('Error fetching data:', err);
                setError(err.response?.data?.message || 'Failed to load data. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [isEditing, taskId, canManageFullForm, isFieldAgent, currentUserId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // --- FIELD AGENT RESTRICTION LOGIC ---
        // If the current user is the assigned agent, only allow them to change status and description.
        if (isAssignedAgent) {
             if (name !== 'status' && name !== 'task_description') {
                 // Ignore changes to other fields like title, dates, or case_id
                 return;
             }
        }
        // -------------------------------------

        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitStatus('submitting');
        const token = localStorage.getItem('token');

        // --- FIELD AGENT SUBMISSION SANITIZATION ---
        let payload = formData;
        if (isAssignedAgent) {
            // For Field Agents, only send the editable fields (status and description)
            // The backend must be robust enough to handle partial PUT updates (using COALESCE/IFNULL)
            payload = {
                status: formData.status,
                task_description: formData.task_description,
                // We MUST include case_id for the backend checkCaseOrgAccess middleware to work!
                case_id: formData.case_id 
            };
        }
        // ------------------------------------------

        try {
            if (isEditing) {
                await axios.put(`${API_BASE_URL}/tasks/${taskId}`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setSubmitStatus('success');
                // Redirect back to the agent's tasks list after update
                setTimeout(() => navigate(`/tasks/user/${currentUserId}`), 1500); 
            } else {
                // POST logic is usually only available to Admin/Manager (canManageFullForm)
                await axios.post(`${API_BASE_URL}/tasks`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setSubmitStatus('success');
                setTimeout(() => navigate('/tasks'), 1500);
            }
        } catch (err) {
            setSubmitStatus('error');
            setError(err.response?.data?.message || 'Submission failed. Please try again.');
        }
    };

    if (loading) return <div className="p-6 text-center">Loading form...</div>;
    // Check if task exists and if the agent is restricted from viewing/editing
    if (error && submitStatus !== 'submitting') return <div className="p-6 text-center text-red-500">{error}</div>;


    // --- Determine Disabled State for Fields ---
    const isTaskTitleDisabled = isEditing && isAssignedAgent;
    const isTimeDisabled = isEditing && isAssignedAgent;
    const isCaseSelectDisabled = isEditing && isAssignedAgent;
    const isUserSelectDisabled = isEditing && isAssignedAgent;
    // Agent can edit description, so it is NOT disabled.
    // Agent can edit status, so it is NOT disabled.

    // If we're in edit mode, but the user is NOT the assigned agent and NOT a manager, show error
    if (isEditing && isFieldAgent && parseInt(formData.user_id) !== parseInt(currentUserId)) {
        return <div className="p-6 text-center text-red-500">Access Denied: This task is assigned to another user.</div>;
    }
    // If we're creating a task but the user is an agent, prevent access (only admin/manager create)
    if (!isEditing && isFieldAgent) {
         return <div className="p-6 text-center text-red-500">Access Denied: Only Case Managers and Admins can create new tasks.</div>;
    }

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">
                {isEditing ? `Edit Task: ${formData.task_title}` : 'Schedule a New Task'}
            </h1>
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6 space-y-6">
                
                {/* Task Title */}
                <div>
                    <label className="block text-sm font-medium text-gray-700">Task Title</label>
                    <input 
                        type="text" 
                        name="task_title" 
                        value={formData.task_title} 
                        onChange={handleChange} 
                        required 
                        disabled={isTaskTitleDisabled}
                        className={`shadow-sm border border-gray-300 text-sm rounded-lg focus:outline-none focus:ring-4 block w-full p-1.5 mt-2 ${isTaskTitleDisabled ? 'bg-gray-200 text-gray-600' : 'bg-gray-50 text-gray-900 focus:ring-indigo-200'}`}
                    />
                </div>
                
                {/* Task Description */}
                <div>
                    <label className="block text-sm font-medium text-gray-700">Description / Agent Notes</label>
                    <textarea 
                        name="task_description" 
                        rows="3" 
                        value={formData.task_description} 
                        onChange={handleChange} 
                        // Field Agent can edit this to add notes
                        className={`shadow-sm border border-gray-300 text-sm rounded-lg focus:outline-none focus:ring-4 block w-full p-1.5 mt-2 ${isAssignedAgent ? 'bg-yellow-50 text-gray-900 focus:ring-yellow-200' : 'bg-gray-50 text-gray-900 focus:ring-indigo-200'}`}
                    ></textarea>
                </div>

                {/* Time fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Start Time</label>
                        <input 
                            type="datetime-local" 
                            name="start_time" 
                            value={formData.start_time} 
                            onChange={handleChange} 
                            required 
                            disabled={isTimeDisabled}
                            className={`shadow-sm border border-gray-300 text-sm rounded-lg focus:outline-none focus:ring-4 block w-full p-1.5 mt-2 ${isTimeDisabled ? 'bg-gray-200 text-gray-600' : 'bg-gray-50 text-gray-900 focus:ring-indigo-200'}`}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">End Time (Due)</label>
                        <input 
                            type="datetime-local" 
                            name="end_time" 
                            value={formData.end_time} 
                            onChange={handleChange} 
                            required 
                            disabled={isTimeDisabled}
                            className={`shadow-sm border border-gray-300 text-sm rounded-lg focus:outline-none focus:ring-4 block w-full p-1.5 mt-2 ${isTimeDisabled ? 'bg-gray-200 text-gray-600' : 'bg-gray-50 text-gray-900 focus:ring-indigo-200'}`}
                        />
                    </div>
                </div>

                {/* Case Selection */}
                <div>
                    <label className="block text-sm font-medium text-gray-700">Assign to Case</label>
                    <select 
                        name="case_id" 
                        value={formData.case_id} 
                        onChange={handleChange} 
                        required 
                        disabled={isCaseSelectDisabled}
                        className={`shadow-sm border border-gray-300 text-sm rounded-lg focus:outline-none focus:ring-4 block w-full p-1.5 mt-2 ${isCaseSelectDisabled ? 'bg-gray-200 text-gray-600' : 'bg-gray-50 text-gray-900 focus:ring-indigo-200'}`}
                    >
                        <option value="">Select a Case</option>
                        {cases.map(caseItem => (
                            <option key={caseItem.id} value={caseItem.id}>{caseItem.case_name} ({caseItem.case_number})</option>
                        ))}
                        {/* If disabled, ensure the current case option is displayed even if not in the 'cases' array */}
                         {isCaseSelectDisabled && !cases.find(c => c.id === formData.case_id) && (
                            <option key={formData.case_id} value={formData.case_id}>{`Case ID: ${formData.case_id} (Locked)`}</option>
                        )}
                    </select>
                </div>

                {/* User Assignment */}
                <div>
                    <label className="block text-sm font-medium text-gray-700">Assign to User</label>
                    <select 
                        name="user_id" 
                        value={formData.user_id} 
                        onChange={handleChange} 
                        required 
                        disabled={isUserSelectDisabled}
                        className={`shadow-sm border border-gray-300 text-sm rounded-lg focus:outline-none focus:ring-4 block w-full p-1.5 mt-2 ${isUserSelectDisabled ? 'bg-gray-200 text-gray-600' : 'bg-gray-50 text-gray-900 focus:ring-indigo-200'}`}
                    >
                        <option value="">Select a User</option>
                        {users.map(userItem => (
                            <option key={userItem.id} value={userItem.id}>{userItem.first_name} {userItem.last_name} ({formatRoleName(userItem.role_name)})</option>
                        ))}
                         {/* If disabled, ensure the current user option is displayed */}
                         {isUserSelectDisabled && !users.find(u => u.id === formData.user_id) && (
                             <option key={formData.user_id} value={formData.user_id}>{`${currentUser.firstName} ${currentUser.lastName} (Locked)`}</option>
                         )}
                    </select>
                </div>
                
                {/* Status (Editable by Agent) */}
                {isEditing && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <select 
                            name="status" 
                            value={formData.status} 
                            onChange={handleChange} 
                            className={`shadow-sm border border-gray-300 text-sm rounded-lg focus:outline-none focus:ring-4 block w-full p-1.5 mt-2 ${isAssignedAgent ? 'bg-green-50 text-gray-900 focus:ring-green-200' : 'bg-gray-50 text-gray-900 focus:ring-indigo-200'}`}
                        >
                            <option value="pending">Pending</option>
                            <option value="in-progress">In-Progress</option>
                            <option value="completed">Completed</option>
                            <option value="on-hold">On Hold</option>
                        </select>
                    </div>
                )}
                
                {/* Action buttons */}
                <div className="flex justify-end space-x-4">
                    <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition duration-300">
                        Cancel
                    </button>
                    {/* Only show Save button if Admin/Manager OR the assigned agent */}
                    {(canManageFullForm || isAssignedAgent) && (
                        <button type="submit" disabled={submitStatus === 'submitting'} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition duration-300 disabled:opacity-50">
                            <BiSave className="inline-block mr-2" />
                            {isEditing ? (submitStatus === 'submitting' ? 'Updating...' : 'Save Changes') : (submitStatus === 'submitting' ? 'Scheduling...' : 'Schedule Task')}
                        </button>
                    )}
                </div>
                {submitStatus === 'success' && <p className="mt-4 text-green-500 text-center">Task {isEditing ? 'updated' : 'scheduled'} successfully!</p>}
            </form>
        </div>
    );
};

export default TaskForm;